import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Printer, ClipboardList, Loader2, Download, Copy, RotateCcw } from "lucide-react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { StatsBar } from "@/components/StatsBar";
import { UploadZone } from "@/components/UploadZone";
import { MarketplaceBadge } from "@/components/badges";
import { WarehousePrintGrid } from "@/components/WarehousePrintGrid";
import { DailySummary } from "@/components/DailySummary";
import { PendingBox } from "@/components/PendingBox";
import { cn } from "@/lib/utils";
import { PrintDialog } from "@/components/PrintDialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busyPdf, setBusyPdf] = useState(false);
  const [marketplace, setMarketplace] = useState("auto");
  const [warehouses, setWarehouses] = useState([]);
  const [printIds, setPrintIds] = useState(null);
  const [busyWh, setBusyWh] = useState(null);

  const load = useCallback(async () => {
    const [s, b, w] = await Promise.all([api.get("/stats"), api.get("/batches"), api.get("/warehouses")]);
    setStats(s.data);
    setBatches(b.data.slice(0, 6));
    setWarehouses(w.data);
    return b.data;
  }, []);
  useEffect(() => { load(); }, [load]);

  const processing = batches.some((b) => b.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [processing, load]);

  const uploadPdfs = async (files) => {
    const bad = files.find((f) => !f.name.toLowerCase().endsWith(".pdf"));
    if (bad) return toast.error(`${bad.name} is not a PDF`);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("marketplace", marketplace);
    setBusyPdf(true);
    try {
      const { data } = await api.post("/uploads", fd);
      const total = data.batches.reduce((a, b) => a + b.total_pages, 0);
      toast.success(`${data.batches.length} file(s) queued · ${total} pages are being read in the background`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPdf(false);
    }
  };

  const printWarehouse = async (wh) => {
    setBusyWh(wh);
    try {
      const { data } = await api.get("/labels/ids", { params: { status: "pending", warehouse_id: wh } });
      if (!data.ids.length) return toast.info("No pending labels for this warehouse");
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyWh(null); }
  };

  const printAllPending = async () => {
    try {
      const { data } = await api.get("/labels/ids", { params: { status: "pending" } });
      if (!data.ids.length) return toast.info("No pending labels");
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printUnmatched = async () => {
    setBusyWh("UNMATCHED");
    try {
      const { data } = await api.get("/labels/ids", { params: { status: "pending", warehouse_id: "unmatched" } });
      if (!data.ids.length) return toast.info("No pending unmatched labels");
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyWh(null); }
  };

  const printBatch = async (b) => {
    try {
      const { data } = await api.get("/labels/ids", { params: { batch_id: b.id, status: "pending" } });
      if (!data.ids.length) return toast.info("No pending labels in this batch");
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const retryBatch = async (id) => {
    try { await api.post(`/batches/${id}/retry`); toast.success("Resuming batch…"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportUnmatched = async () => {
    try { await downloadFile("/labels/unmatched-export", "unmatched-skus.csv"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Command center</div>
          <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800"><Link to="/picklist" data-testid="picklist-view-button"><ClipboardList className="mr-2 h-4 w-4" /> Picklist</Link></Button>
          <Button asChild className="bg-blue-600 hover:bg-blue-700"><Link to="/labels?status=pending" data-testid="go-print-queue"><Printer className="mr-2 h-4 w-4" /> Print queue</Link></Button>
        </div>
      </div>

      <StatsBar stats={stats} />

      {stats?.unmatched > 0 && (
        <div data-testid="unmatched-warning-alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-700/60 bg-red-950/40 px-5 py-4">
          <div className="flex items-center gap-3 text-red-200">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <span><strong>{stats.unmatched}</strong> label(s) have no SKU → Warehouse match. Upload the mapping file or assign them manually so nothing is skipped.</span>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-unmatched-button" size="sm" variant="outline" className="border-red-700 text-red-200 hover:bg-red-900/40" onClick={exportUnmatched}><Download className="mr-1 h-4 w-4" /> Download unmatched CSV</Button>
            <Button asChild size="sm" variant="outline" className="border-red-700 text-red-200 hover:bg-red-900/40"><Link to="/labels?warehouse=unmatched">Fix now <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
          </div>
        </div>
      )}

      {stats?.duplicates > 0 && (
        <div data-testid="duplicate-warning-alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-orange-700/60 bg-orange-950/40 px-5 py-4">
          <div className="flex items-center gap-3 text-orange-200">
            <Copy className="h-5 w-5 text-orange-400" />
            <span><strong>{stats.duplicates}</strong> label(s) share a tracking ID with another label — check them before printing so nothing ships twice.</span>
          </div>
          <Button asChild size="sm" variant="outline" className="border-orange-700 text-orange-200 hover:bg-orange-900/40"><Link to="/labels?status=all&duplicates=1&date=all" data-testid="view-duplicates-link">Review duplicates <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
      )}

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl">Upload shipping labels</h2>
            <Select value={marketplace} onValueChange={setMarketplace}>
              <SelectTrigger data-testid="upload-marketplace-select" className="w-40 border-slate-700 bg-slate-950 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="amazon">Amazon</SelectItem>
                <SelectItem value="flipkart">Flipkart</SelectItem>
                <SelectItem value="firstcry">Firstcry</SelectItem>
                <SelectItem value="myntra">Myntra</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <UploadZone testId="upload-pdf-dropzone" title="Drop label PDFs here" hint="Amazon · Flipkart · Firstcry — multiple PDFs allowed, each page becomes one label. Image-only labels (Amazon) are read with OCR." accept="application/pdf,.pdf" multiple onFiles={uploadPdfs} busy={busyPdf} />
          {batches.filter((b) => b.status === "processing").map((b) => (
            <div key={b.id} data-testid={`batch-progress-${b.id}`} className="rounded-md border border-blue-800/60 bg-[#111827] p-4">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 truncate font-mono text-xs text-slate-200"><Loader2 className="h-3.5 w-3.5 animate-spin text-blue-300" />{b.file_name}</span>
                <span className="font-mono text-xs text-blue-300">{b.processed} / {b.total_pages} pages</span>
              </div>
              <Progress value={b.total_pages ? (b.processed / b.total_pages) * 100 : 0} className="mt-2 h-1.5 bg-slate-800" />
            </div>
          ))}
        </div>
        <PendingBox stats={stats} warehouses={warehouses} onPrintAll={printAllPending} onPrintUnmatched={printUnmatched} />
      </div>

      <WarehousePrintGrid warehouses={warehouses} onPrint={printWarehouse} onPrintUnmatched={printUnmatched} busyId={busyWh} />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl">Recent batches</h2>
          <Link to="/batches" className="text-sm text-blue-300 hover:underline" data-testid="view-all-batches">View all</Link>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="recent-batches">
          {batches.length === 0 && <div className="text-sm text-slate-500">No uploads yet.</div>}
          {batches.map((b) => (
            <div key={b.id} className={cn("stat-card rounded-md border bg-[#111827] p-4 hover:border-blue-500/50", b.duplicates > 0 ? "border-orange-700/60" : "border-slate-800")} data-testid={`batch-card-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <Link to={`/labels?batch=${b.id}&status=all`} className="truncate font-mono text-xs text-slate-200 hover:text-blue-300">{b.file_name}</Link>
                {b.marketplace_hint !== "auto" && <MarketplaceBadge marketplace={b.marketplace_hint} />}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-slate-400">
                {b.status === "processing" ? <span className={b.stalled ? "text-amber-300" : "text-blue-300"}>{b.processed}/{b.total_pages} {b.stalled ? "stalled — click Retry" : "reading…"}</span> : <span>{b.processed}/{b.total_pages} pages</span>}
                {b.status === "error" && <span className="text-red-400" data-testid={`batch-error-${b.id}`}>error: {b.error}</span>}
                <span className="text-emerald-400">{b.printed} printed</span>
                {b.pending > 0 && <span className="text-amber-300">{b.pending} pending</span>}
                {b.unmatched > 0 && <span className="text-red-400">{b.unmatched} unmatched</span>}
                {b.duplicates > 0 && <Link to={`/labels?batch=${b.id}&status=all&duplicates=1`} className="flex items-center gap-1 text-orange-300 hover:underline" data-testid={`batch-duplicates-${b.id}`}><Copy className="h-3 w-3" />{b.duplicates} duplicate{b.duplicates > 1 ? "s" : ""}</Link>}
              </div>
              <div className="mt-3 flex gap-2">
                <Button data-testid={`batch-print-${b.id}`} size="sm" className="h-8 flex-1 bg-blue-600 hover:bg-blue-700" disabled={!b.pending || b.status !== "done"} onClick={() => printBatch(b)}>
                  <Printer className="mr-1 h-3.5 w-3.5" /> Print {b.pending} pending
                </Button>
                {(b.status === "error" || b.stalled) && b.file_id && (
                  <Button data-testid={`batch-retry-${b.id}`} size="sm" variant="outline" className="h-8 border-amber-700 text-amber-300 hover:bg-amber-900/40" onClick={() => retryBatch(b.id)}><RotateCcw className="mr-1 h-3 w-3" /> Retry</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <DailySummary refreshKey={stats?.printed} />

      <PrintDialog labelIds={printIds} open={!!printIds} onOpenChange={(o) => !o && setPrintIds(null)} onCompleted={load} onReplaceIds={setPrintIds} />
    </div>
  );
}
